import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import '../styles/AnimatedLoading.css';

function AnimatedLoading() {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const fullText = "QUANTUM FRAME STUDIO";

  // Simulate loading progress with smoother increments
  useEffect(() => {
    // Use a simpler, more efficient progress calculation
    const totalDuration = 2800; // slightly less than the 3000ms in App.js
    const updateInterval = 40; // Increased interval for better performance
    const totalSteps = totalDuration / updateInterval;
    let step = 0;

    const interval = setInterval(() => {
      step++;
      // Linear progress for better performance
      const progress = Math.min(100, Math.floor((step / totalSteps) * 100));
      setLoadingProgress(progress);

      if (progress >= 100) {
        clearInterval(interval);
      }
    }, updateInterval);

    return () => clearInterval(interval);
  }, []);

  // Simplified typewriter effect
  useEffect(() => {
    const letterDelay = 2800 / (fullText.length + 5);
    let currentIndex = 0;

    const textInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setLoadingText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(textInterval);
      }
    }, letterDelay);

    return () => clearInterval(textInterval);
  }, [fullText]);

  // Simplified animation variants
  const logoVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const progressVariants = {
    initial: { width: 0 },
    animate: {
      width: `${loadingProgress}%`,
      transition: {
        duration: 0.3,
        ease: "linear"
      }
    }
  };

  // Reduced number of particles for better performance
  const particles = Array.from({ length: 12 }).map((_, index) => (
    <div
      key={index}
      className="quantum-particle"
      style={{
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: `${Math.random() * 3 + 2}px`,
        height: `${Math.random() * 3 + 2}px`,
        backgroundColor: index % 3 === 0 ? 'var(--color-accent)' :
                         index % 3 === 1 ? 'var(--color-secondary)' :
                         'var(--color-text)',
        animationDelay: `${Math.random() * 2}s`,
        animationDuration: `${Math.random() * 3 + 3}s`
      }}
    />
  ));

  return (
    <div className="animated-loading">
      <div className="quantum-loading-container">
        {/* Static particles instead of animated ones */}
        <div className="particles-container">
          {particles}
        </div>

        {/* Logo container with animated rings */}
        <motion.div
          className="quantum-logo-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Multiple animated rings */}
          <div className="logo-ring ring-outer"></div>
          <div className="logo-ring ring-middle"></div>
          <div className="logo-ring ring-inner"></div>

          {/* Logo */}
          <motion.div
            className="quantum-logo"
            variants={logoVariants}
            initial="hidden"
            animate="visible"
          >
            <img
              src="/assets/images/LOGO.png"
              alt="QuantumFrame Studio"
              className="actual-logo"
              onError={(e) => {
                console.error("Error loading logo:", e);
                // Simple SVG fallback
                e.target.src = "/assets/images/quantumframe-logo.svg";
              }}
            />
          </motion.div>
        </motion.div>

        <motion.div
          className="quantum-text"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h2>{loadingText}</h2>
        </motion.div>

        <div className="quantum-progress-container">
          <motion.div
            className="quantum-progress-bar"
            variants={progressVariants}
            initial="initial"
            animate="animate"
          />
          <div className="quantum-progress-text">{loadingProgress}%</div>
        </div>
      </div>
    </div>
  );
}

export default AnimatedLoading;
